// Service API pour remplacer GraphQL par des appels REST
import authService from './authService'
import dexieService from './dexieService'
import pako from 'pako'

import { VUE_APP_API_URL } from '@/utils/env'

const API_BASE_URL = VUE_APP_API_URL

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL
  }

  async request(endpoint, options = {}) {
    // Utiliser le service d'authentification pour les requêtes authentifiées
    const url = `${this.baseURL}${endpoint}`
    
    try {
      const response = await authService.makeAuthenticatedRequest(url, options)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        // Utiliser le message détaillé si disponible, sinon l'erreur générique
        const errorMessage = errorData.message || errorData.error || `API Error: ${response.status} ${response.statusText}`
        throw new Error(errorMessage)
      }
      
      return response.json()
    } catch (error) {
      // Si c'est une erreur d'authentification, la propager
      if (error.message.includes('Session expirée')) {
        throw error
      }
      
      // Pour les autres erreurs, essayer une requête simple
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      }

      const response = await fetch(url, config)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        // Utiliser le message détaillé si disponible, sinon l'erreur générique
        const errorMessage = errorData.message || errorData.error || `API Error: ${response.status} ${response.statusText}`
        throw new Error(errorMessage)
      }
      
      return response.json()
    }
  }

  async publicRequest(endpoint, options = {}) {
    // Requête publique sans authentification
    const url = `${this.baseURL}${endpoint}`
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    }

    const response = await fetch(url, config)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `API Error: ${response.status} ${response.statusText}`)
    }
    
    return response.json()
  }

  // ==================== MÉTHODES UTILITAIRES GÉNÉRIQUES ====================

  /**
   * Récupère des données depuis le cache ou l'API avec un pattern générique
   * @param {string} tableName - Nom de la table dans dexieService
   * @param {string} endpoint - Endpoint API à appeler
   * @param {Function} formatFunction - Fonction pour formater les données (optionnel)
   * @returns {Promise<Array>}
   */
  async getEntityWithCache(tableName, endpoint, formatFunction = null) {
    try {
      // Vérifier d'abord le cache local
      const dexieModule = await import('./dexieService')
      const table = dexieModule[tableName]
      const cachedData = await table().toArray()
      
      if (cachedData && cachedData.length > 0) {
        return cachedData
      }

      // Récupérer depuis l'API
      const data = await this.request(endpoint)
      
      // Formater les données si une fonction est fournie
      const formattedData = formatFunction && Array.isArray(data) 
        ? data.map(formatFunction) 
        : (Array.isArray(data) ? data : [])
      
      // Sauvegarder en cache local
      if (formattedData.length > 0) {
        await this.saveToCacheGeneric(tableName, formattedData)
      }
      
      return formattedData
    } catch (error) {
      console.error(`Erreur lors de la récupération de ${tableName}:`, error)
      return []
    }
  }

  /**
   * Sauvegarde des données dans le cache de manière générique
   * @param {string} tableName - Nom de la table dans dexieService
   * @param {Array} data - Données à sauvegarder
   * @returns {Promise<boolean>}
   */
  async saveToCacheGeneric(tableName, data) {
    try {
      const dexieModule = await import('./dexieService')
      const table = dexieModule[tableName]
      
      // Vider la table avant d'insérer les nouvelles données
      await table().clear()
      
      // Insérer les nouvelles données
      await table().bulkPut(data)
      
      return true
    } catch (error) {
      console.error(`Erreur lors de la sauvegarde de ${tableName} en cache:`, error)
      return false
    }
  }

  /**
   * Charge des données depuis l'API avec vérification d'authentification
   * @param {string} tableName - Nom de la table
   * @param {Function} getterFunction - Fonction à appeler pour récupérer les données
   * @returns {Promise<Array>}
   */
  async loadEntityFromAPI(tableName, getterFunction) {
    try {
      // Vérifier d'abord si l'utilisateur est authentifié
      const authServiceModule = (await import('./authService')).default
      const isAuthenticated = await authServiceModule.isAuthenticated()
      
      if (!isAuthenticated) {
        return []
      }
      
      const data = await getterFunction.call(this)
      
      return data
    } catch (error) {
      console.error(`Erreur lors du chargement de ${tableName} depuis l'API:`, error)
      return []
    }
  }

  /**
   * Décompresse des données gzip et retourne le JSON parsé
   * @param {ArrayBuffer} compressedData - Données compressées
   * @returns {Promise<any>}
   */
  async decompressGzipData(compressedData) {
    // ─── Stratégie mémoire ────────────────────────────────────────────────
    // Pour un export de 29 000 lots, le JSON décompressé peut dépasser 200 Mo.
    // Le pic mémoire est : compressedData + intermédiaire de décompression +
    // string JSON + objet parsé.  On minimise les couches intermédiaires et on
    // libère les références dès que possible pour permettre au GC d'intervenir :
    //
    //  1. DecompressionStream (natif, Chrome 80+, Firefox 113+, Safari 16.4+)
    //     → Blob créé depuis compressedData, puis compressedData= null libère
    //       l'ArrayBuffer original AVANT le streaming (le Blob en a sa propre
    //       copie côté navigateur). Response.text() streame sans Uint8Array
    //       intermédiaire.  C'est la voie la moins gourmande.
    //
    //  2. pako (fallback) → on libère compressedData avant inflate, et le
    //     Uint8Array décompressé avant JSON.parse pour minimiser le pic.
    //     TextDecoder évite l'Array.join() qui causait RangeError sur de
    //     très longues chaînes.
    // ─────────────────────────────────────────────────────────────────────

    if (typeof DecompressionStream !== 'undefined') {
      // Créer le Blob depuis le buffer compressé, puis libérer immédiatement
      // la référence locale : le navigateur a sa propre copie dans le Blob,
      // l'ArrayBuffer original peut être récupéré par le GC pendant le streaming.
      const blob = new Blob([compressedData])
      compressedData = null // eslint-disable-line no-param-reassign
      const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'))
      const jsonText = await new Response(stream).text()
      return JSON.parse(jsonText)
    }

    if (typeof pako !== 'undefined') {
      // Créer le Uint8Array depuis le buffer, puis libérer l'ArrayBuffer original
      // avant inflate pour éviter de cumuler les deux copies compressées en RAM.
      const uint8 = new Uint8Array(compressedData)
      compressedData = null // eslint-disable-line no-param-reassign
      const decompressed = pako.inflate(uint8)
      // uint8 peut maintenant être libéré (pako a créé son propre buffer)
      const jsonText = new TextDecoder().decode(decompressed)
      return JSON.parse(jsonText)
    }

    throw new Error('Aucune méthode de décompression disponible. Installez pako ou utilisez un navigateur moderne.')
  }

  /**
   * Récupère un export gzip depuis l'API.
   *
   * Améliorations :
   *  - AbortController avec timeout de 10 min (évite les blocages infinis en cas de "stall" réseau)
   *  - Lecture via ReadableStream pour émettre la progression en octets pendant le téléchargement
   *
   * @param {string}        entityName               - Nom de l'entité (controls, properties, etc.)
   * @param {Function|null} downloadProgressCallback - Appelé avec (downloadedBytes, totalBytes)
   *                                                   pendant le téléchargement. totalBytes = 0
   *                                                   si Content-Length n'est pas disponible.
   * @returns {Promise<{data: Array, exportGeneratedAt: string}>}
   */
  async getEntityFromExport(entityName, downloadProgressCallback = null) {
    // AbortController avec timeout de 10 minutes pour les gros fichiers.
    // Évite un blocage infini si le serveur ne répond plus.
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000)

    try {
      const url = `${this.baseURL}/${entityName}/export`
      const response = await authService.makeAuthenticatedRequest(url, {
        signal: controller.signal
      })
      
      if (!response.ok) {
        clearTimeout(timeoutId)
        throw new Error(`Erreur HTTP: ${response.status}`)
      }
      
      // Extraire la date de génération du header
      const exportGeneratedAt = response.headers.get('x-export-generated-at')
      // Content-Length peut être absent (Transfer-Encoding: chunked)
      const contentLength = parseInt(response.headers.get('content-length') || '0', 10)
      
      let compressedData

      // ── Lecture du body via ReadableStream ───────────────────────────────
      // Les deux chemins (avec ou sans callback de progression) passent par
      // le même ReadableStream pour assembler les chunks en un seul Uint8Array.
      // Cela évite un double-buffering (chunks individuels + merged) et permet
      // de libérer les chunks au fur et à mesure.
      //
      // Si response.body n'est pas disponible (très vieux navigateurs), on
      // tombe en fallback sur response.arrayBuffer().
      if (response.body) {
        const reader = response.body.getReader()
        const chunks = []
        let downloadedBytes = 0
        let streamDone = false

        while (!streamDone) {
          const { done, value } = await reader.read() // eslint-disable-line no-await-in-loop
          if (done) {
            streamDone = true
          } else {
            chunks.push(value)
            downloadedBytes += value.length
            if (downloadProgressCallback) {
              downloadProgressCallback(downloadedBytes, contentLength)
            }
          }
        }

        // Assembler tous les chunks en un seul ArrayBuffer
        const totalLength = chunks.reduce((sum, c) => sum + c.length, 0)
        const merged = new Uint8Array(totalLength)
        let offset = 0
        for (const chunk of chunks) {
          merged.set(chunk, offset)
          offset += chunk.length
        }
        // Libérer les Uint8Array individuels dès que le buffer consolidé est prêt,
        // pour éviter d'avoir chunks + merged en mémoire simultanément lors de
        // la décompression (pic mémoire → "out of memory" sur les gros exports).
        chunks.length = 0
        compressedData = merged.buffer
      } else {
        // Fallback : navigateurs sans ReadableStream (très rare)
        compressedData = await response.arrayBuffer()
      }

      clearTimeout(timeoutId)

      // Donner le contrôle au navigateur AVANT la décompression synchrone (pako/JSON.parse)
      // afin que Vue puisse mettre à jour le DOM (ex: afficher "Traitement en cours…").
      // setTimeout(0) crée une macrotask, qui s'exécute après toutes les microtasks en attente
      // (dont les watchers Vue), permettant l'affichage du bon message à l'écran.
      await new Promise(resolve => setTimeout(resolve, 0))

      // Lire et décompresser les données.
      // On passe la référence compressedData à decompressGzipData qui la met à null en interne
      // dès que le Blob/Uint8Array est créé.  On libère également ici après l'appel
      // pour que l'ArrayBuffer ne soit plus référencé dans ce scope pendant JSON.parse.
      const data = await this.decompressGzipData(compressedData)
      compressedData = null // eslint-disable-line no-param-reassign

      if (Array.isArray(data)) {
        return { data, exportGeneratedAt }
      } else {
        console.warn('Format de réponse inattendu:', typeof data)
        return { data: [], exportGeneratedAt }
      }
    } catch (error) {
      clearTimeout(timeoutId)
      if (error.name === 'AbortError') {
        const msg = `Téléchargement de l'export ${entityName} abandonné (timeout 10 min)`
        console.error(`[API] ${msg}`)
        throw new Error(msg)
      }
      console.error(`Erreur lors de la récupération du fichier d'export de ${entityName}:`, error)
      throw error
    }
  }

  /**
   * Charge et sauvegarde des données depuis un fichier d'export
   * @param {string} entityName - Nom de l'entité (controls, properties, etc.)
   * @param {string} tableName - Nom de la table dans dexieService
   * @param {Function} progressCallback - Callback pour le suivi de progression
   * @returns {Promise<{success: boolean, count: number, exportGeneratedAt?: string, skipped?: boolean, error?: string}>}
   */
  async loadAndSaveFromExport(entityName, tableName, progressCallback = null) {
    try {
      // Vérifier l'authentification
      const authServiceModule = (await import('./authService')).default
      const isAuthenticated = await authServiceModule.isAuthenticated()
      
      if (!isAuthenticated) {
        return { success: false, count: 0 }
      }
      
      // Vérifier si la table est déjà remplie
      const dexieModule = await import('./dexieService')
      const table = dexieModule[tableName]
      const syncTable = dexieModule.sync
      const existingCount = await table().count()
      
      if (existingCount > 0) {
        return { success: true, count: existingCount, skipped: true }
      }
      
      // Récupérer les données depuis l'export
      const { data, exportGeneratedAt } = await this.getEntityFromExport(entityName)
      
      if (!data || data.length === 0) {
        console.warn(`Aucune donnée récupérée depuis le fichier d'export de ${entityName}`)
        return { success: false, count: 0 }
      }
      
      // Insérer par batch pour éviter les problèmes de mémoire
      const batchSize = 1000
      let saved = 0
      
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize)
        await table().bulkPut(batch)
        saved += batch.length
        
        // Appeler le callback de progression si fourni
        if (progressCallback && typeof progressCallback === 'function') {
          progressCallback(saved, data.length)
        }
      }
      
      // Sauvegarder les informations de synchronisation.
      // On utilise l'heure courante (fin du chargement) et non exportGeneratedAt
      // pour éviter que tous les enregistrements importés après la génération de
      // l'export apparaissent comme "modifiés" lors de la prochaine sync incrémentale.
      try {
        await syncTable().put({
          entity: entityName,
          lastSyncDate: new Date().toISOString(),
          exportGenerationDate: exportGeneratedAt || null
        })
      } catch (syncError) {
        console.error('Erreur lors de la sauvegarde des infos de sync:', syncError)
      }
      
      return { success: true, count: saved, exportGeneratedAt }
    } catch (error) {
      console.error(`Erreur lors du chargement et de la sauvegarde de ${entityName} depuis l'export:`, error)
      return { success: false, count: 0, error: error.message }
    }
  }

  // ==================== MÉTHODES POUR REMPLACER LES REQUÊTES GRAPHQL ====================
  
  // Authentification
  async login(username, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    })
  }

  async getCurrentUser() {
    return this.request('/user/me')
  }

  async getVisits(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString()
    return this.request(`/visits?${queryParams}`)
  }

  async getActions(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString()
    return this.request(`/actions?${queryParams}`)
  }

  async createVisit(data) {
    return this.request('/visits', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateVisit(id, data) {
    return this.request(`/visits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  // Attribution
  async getAttributions() {
    return this.request('/attributions')
  }

  async createAttribution(data) {
    return this.request('/attributions', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // Métriques
  async getMetrics(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString()
    return this.request(`/metrics?${queryParams}`)
  }

  // Export
  async exportVisit(visitId, format = 'pdf') {
    return this.request(`/visits/${visitId}/export?format=${format}`)
  }

  // Upload d'images
  async uploadImage(file, visitId) {
    const formData = new FormData()
    formData.append('image', file)
    formData.append('visitId', visitId)

    return this.request('/images/upload', {
      method: 'POST',
      body: formData,
      headers: {} // Laisser le navigateur définir le Content-Type pour FormData
    })
  }

  // Nettoyer les données locales
  async clearLocalData() {
    await dexieService.clearAllData()
  }

  // ==================== DICTIONNAIRES ====================

  async getDictionary(name) {
    try {
      // Vérifier d'abord si on a les données en cache local
      const cachedDictionary = await dexieService.getDictionary(name)
      if (cachedDictionary && Object.keys(cachedDictionary).length > 0) {
        return cachedDictionary
      }

      // Sinon, récupérer depuis l'API
      const data = await this.request(`/dictionaries/${name}`)
      
      return data
    } catch (error) {
      console.error(`Erreur lors de la récupération du dictionnaire ${name}:`, error)
      // Retourner un dictionnaire vide en cas d'erreur
      return { data: {} }
    }
  }

  // Charger tous les dictionnaires depuis l'API
  async loadAllDictionaries() {
    try {
      // Vérifier d'abord si l'utilisateur est authentifié
      const authService = (await import('./authService')).default
      const isAuthenticated = await authService.isAuthenticated()
      
      if (!isAuthenticated) {
        return {}
      }
      
      // Récupérer tous les dictionnaires en une seule requête
      const data = await this.request('/dictionaries/all')
      
      if (data && typeof data === 'object') {
        // Parcourir chaque dictionnaire et le sauvegarder
        for (const [dictionaryName, dictionaryData] of Object.entries(data)) {

          
          // Cas spécial pour les métadonnées de pré-chiffrage
          if (dictionaryName === 'prechiffrage_metadata') {
            // Sauvegarder les métadonnées de pré-chiffrage dans IndexDB
            await dexieService.saveDictionary('prechiffrage_work_fields', dictionaryData.work_fields || [])
            await dexieService.saveDictionary('prechiffrage_work_types', dictionaryData.work_types || [])
            continue
          }
          
          // L'API retourne directement un objet clé-valeur
          const processedData = dictionaryData || {}
          
          // Sauvegarder en cache local
          await dexieService.saveDictionary(dictionaryName, processedData)
        }
        
        return data
      } else {
        return {}
      }
    } catch (error) {
      console.error('Erreur lors du chargement de tous les dictionnaires:', error)
      return {}
    }
  }

  // Vérifier et charger les dictionnaires si nécessaire
  async ensureDictionariesLoaded() {
    try {
      // Liste des dictionnaires attendus
      const expectedDictionaries = [
        'property_type',
        'visit_status',
        'answer_state', 
        'answer_yesno',
        'rentable',
        'unit',
        'sub_unit',
        'lease_type',
        'legal_owner_division',
        'mandator_division',
        'tenant_division',
        'parent_association_type',
        'progress',
        'work_field',
        'work_type',
        'room_type'
      ]
      
      // Vérifier si tous les dictionnaires attendus sont présents et non vides
      const missingDictionaries = []
      for (const dictName of expectedDictionaries) {
        const cached = await dexieService.getDictionary(dictName)
        if (!cached || Object.keys(cached).length === 0) {
          missingDictionaries.push(dictName)
        }
      }
      
      // Si des dictionnaires sont manquants, charger tous les dictionnaires
      if (missingDictionaries.length > 0) {
        await this.loadAllDictionaries()
      }
      
    } catch (error) {
      console.error('Erreur lors de la vérification des dictionnaires:', error)
      // Si c'est une erreur de session expirée, ne pas la propager
      if (error.message && error.message.includes('Session expirée')) {
        return
      }
    }
  }

  async getPropertyTypes() {
    try {
      // Vérifier d'abord le cache local
      const cached = await dexieService.getDictionary('property_type')
      
      if (cached && Object.keys(cached).length > 0) {
        return cached
      }

      // Forcer l'appel API direct
      const data = await this.request('/dictionaries/property_type')
      
      let propertyTypes = {}
      
      // Essayer différentes structures possibles
      let items = []
      
      if (Array.isArray(data)) {
        items = data
      } else if (data && Array.isArray(data.items)) {
        items = data.items
      } else if (data && data.data && Array.isArray(data.data.items)) {
        items = data.data.items
      } else if (data && data.data && Array.isArray(data.data)) {
        items = data.data
      } else if (data && data['hydra:member'] && Array.isArray(data['hydra:member'])) {
        items = data['hydra:member']
      }
      
      // Convertir les items en objet { key: value }
      if (items.length > 0) {
        propertyTypes = items.reduce((acc, item) => {
          if (item.key !== undefined && item.value !== undefined) {
            acc[item.key] = item.value
          } else if (item.code && item.label) {
            acc[item.code] = item.label
          } else if (item.id && item.name) {
            acc[item.id] = item.name
          }
          return acc
        }, {})
      }
      
      // Sauvegarder en cache local
      await dexieService.saveDictionary('property_type', propertyTypes)
      
      return propertyTypes
    } catch (error) {
      console.error('Erreur lors de la récupération des types de propriétés:', error)
      
      // Si c'est une erreur 404, sauvegarder un dictionnaire vide
      if (error.message.includes('404')) {
        await dexieService.saveDictionary('property_type', {})
      }
      
      return {}
    }
  }

  // ==================== PROPERTIES ====================

  async getPropertiesCount() {
    try {
      const data = await this.request('/properties/count')
      return data.total || data.count || 0
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de propriétés:', error)
      throw error
    }
  }

  // ==================== TEAMS ====================

  async getTeams() {
    return this.getEntityWithCache('teams', '/teams', (team) => ({
      id: team.id,
      name: team.name,
      enabled: team.enabled,
      insertedAt: new Date().toISOString()
    }))
  }

  async saveTeamsToCache(teams) {
    return this.saveToCacheGeneric('teams', teams)
  }

  async loadTeamsFromAPI() {
    return this.loadEntityFromAPI('teams', this.getTeams)
  }

  // ==================== USERS ====================

  async getUsers() {
    return this.getEntityWithCache('users', '/users', (user) => {
      // Formater l'équipe
      let formattedTeam = null
      if (user.team) {
        formattedTeam = {
          id: user.team.id ? String(user.team.id) : null,
          name: user.team.name || null,
          code: user.team.code || user.team.id || null
        }
      }

      // Construire fullName
      let fullName = user.fullName
      if (!fullName && (user.firstName || user.lastName)) {
        fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()
      }

      return {
        id: user.id ? String(user.id) : null,
        enabled: user.enabled !== undefined ? user.enabled : true,
        firstName: user.firstName || null,
        lastName: user.lastName || null,
        fullName: fullName || null,
        team: formattedTeam,
        insertedAt: new Date().toISOString()
      }
    })
  }

  async saveUsersToCache(users) {
    return this.saveToCacheGeneric('users', users)
  }

  async loadUsersFromAPI() {
    return this.loadEntityFromAPI('users', this.getUsers)
  }

  // ==================== ASSISTANCE MANDATORS ====================

  async getAssistanceMandators() {
    return this.getEntityWithCache('assistanceMandators', '/assistance-mandators', (mandator) => ({
      id: mandator.id,
      name: mandator.name,
      division: mandator.division,
      address: mandator.address,
      phoneNumber: mandator.phoneNumber,
      officePhoneNumber: mandator.officePhoneNumber,
      mobilePhoneNumber: mandator.mobilePhoneNumber,
      email: mandator.email,
      startedAt: mandator.startedAt,
      endedAt: mandator.endedAt,
      insertedAt: new Date().toISOString()
    }))
  }

  async saveAssistanceMandatorsToCache(mandators) {
    return this.saveToCacheGeneric('assistanceMandators', mandators)
  }

  async loadAssistanceMandatorsFromAPI() {
    return this.loadEntityFromAPI('assistanceMandators', this.getAssistanceMandators)
  }

  // ==================== MANAGER MANDATORS ====================

  async getManagerMandators() {
    return this.getEntityWithCache('managerMandators', '/manager-mandators', (mandator) => ({
      id: mandator.id,
      name: mandator.name,
      division: mandator.division,
      address: mandator.address,
      phoneNumber: mandator.phoneNumber,
      officePhoneNumber: mandator.officePhoneNumber,
      mobilePhoneNumber: mandator.mobilePhoneNumber,
      email: mandator.email,
      startedAt: mandator.startedAt,
      endedAt: mandator.endedAt,
      insertedAt: new Date().toISOString()
    }))
  }

  async saveManagerMandatorsToCache(mandators) {
    return this.saveToCacheGeneric('managerMandators', mandators)
  }

  async loadManagerMandatorsFromAPI() {
    return this.loadEntityFromAPI('managerMandators', this.getManagerMandators)
  }

  // ==================== LEGAL OWNERS ====================

  async getLegalOwners() {
    return this.getEntityWithCache('legalOwners', '/legal-owners', (owner) => ({
      id: owner.id,
      name: owner.name,
      address: owner.address,
      phoneNumber: owner.phoneNumber,
      officePhoneNumber: owner.officePhoneNumber,
      mobilePhoneNumber: owner.mobilePhoneNumber,
      email: owner.email,
      insertedAt: new Date().toISOString()
    }))
  }

  async saveLegalOwnersToCache(owners) {
    return this.saveToCacheGeneric('legalOwners', owners)
  }

  async loadLegalOwnersFromAPI() {
    return this.loadEntityFromAPI('legalOwners', this.getLegalOwners)
  }

  // ==================== INTERVENTION POINTS ====================

  async getInterventionPoints() {
    try {
      // Vérifier d'abord le cache local
      const { interventionPoints } = await import('./dexieService')
      const cachedPoints = await interventionPoints().toArray()
      
      if (cachedPoints && cachedPoints.length > 0) {
        return cachedPoints
      }
      
      // Récupérer depuis l'API (déjà formatés)
      const pointsData = await this.request('/intervention-points')
      const formattedPoints = Array.isArray(pointsData) ? pointsData : []
      
      return formattedPoints
    } catch (error) {
      console.error('Erreur lors de la récupération des interventionPoints:', error)
      return []
    }
  }

  async saveInterventionPointsToCache(points) {
    try {
      const dexieService = await import('./dexieService')
      const { interventionPoints: pointsTable } = dexieService
      
      await pointsTable().clear()
      await pointsTable().bulkPut(points)
      
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des interventionPoints en cache:', error)
      return false
    }
  }

  async loadInterventionPointsFromAPI() {
    try {
      const authService = (await import('./authService')).default
      const isAuthenticated = await authService.isAuthenticated()
      
      if (!isAuthenticated) {
        return []
      }
      
      const points = await this.getInterventionPoints()
      
      if (points.length > 0) {
        await this.saveInterventionPointsToCache(points)
      }
      
      return points
    } catch (error) {
      console.error('Erreur lors du chargement des interventionPoints depuis l\'API:', error)
      return []
    }
  }

  // ==================== CONTROLS ====================

  async getControlsCount() {
    try {
      const data = await this.request('/controls/count')
      return data.total || data.count || 0
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de controls:', error)
      throw error
    }
  }

  async getControlsBatch(offset = 0, limit = 5000) {
    try {
      const data = await this.request(`/controls/${offset}/${limit}`)
      
      // Extraire les controls selon le format de réponse
      let controls = []
      if (Array.isArray(data)) {
        controls = data
      } else if (data.data && Array.isArray(data.data)) {
        controls = data.data
      } else if (data.items && Array.isArray(data.items)) {
        controls = data.items
      }
      
      return controls
    } catch (error) {
      console.error('Erreur lors de la récupération du batch de controls:', error)
      return []
    }
  }

  async loadControlsFromAPI() {
    return this.loadEntityFromAPI('controls', async () => [])
  }

  async getAllControlsFromExport() {
    return this.getEntityFromExport('controls')
  }

  async loadAndSaveControlsFromExport(progressCallback = null) {
    return this.loadAndSaveFromExport('controls', 'controls', progressCallback)
  }

  // ==================== PROPERTIES (EXPORT) ====================

  async getAllPropertiesFromExport() {
    return this.getEntityFromExport('properties')
  }

  async loadAndSavePropertiesFromExport(progressCallback = null) {
    return this.loadAndSaveFromExport('properties', 'properties', progressCallback)
  }

  // ==================== DATABASE SYNC ====================

  /**
   * Get table names only (fast, no statistics)
   */
  async getDatabaseSyncTablesNames() {
    try {
      const data = await this.request('/database-sync/tables/names')
      return data.data || []
    } catch (error) {
      console.error('Erreur lors de la récupération des noms de tables:', error)
      throw error
    }
  }

  /**
   * Get tables grouped by synchronization level
   * Returns: [{ level: 1, tables: ['team', ...] }, { level: 2, tables: [...] }, ...]
   */
  async getDatabaseSyncTablesLevels() {
    try {
      const data = await this.request('/database-sync/tables/levels')
      return data.data || []
    } catch (error) {
      console.error('Erreur lors de la récupération des niveaux de tables:', error)
      throw error
    }
  }

  /**
   * Get list of all tables with synchronization statistics (slow for many tables)
   */
  async getDatabaseSyncTables() {
    try {
      const data = await this.request('/database-sync/tables')
      return data.data || []
    } catch (error) {
      console.error('Erreur lors de la récupération des tables de synchronisation:', error)
      throw error
    }
  }

  /**
   * Get statistics for a specific table
   */
  async getDatabaseSyncTableStats(tableName) {
    try {
      const data = await this.request(`/database-sync/tables/${tableName}`)
      return data.data || {}
    } catch (error) {
      console.error(`Erreur lors de la récupération des stats de la table ${tableName}:`, error)
      throw error
    }
  }

  /**
   * Compare a specific table between MSSQL and MySQL
   */
  async compareDatabaseSyncTable(tableName) {
    try {
      const data = await this.request(`/database-sync/tables/${tableName}/compare`, {
        method: 'POST'
      })
      return data.data || {}
    } catch (error) {
      console.error(`Erreur lors de la comparaison de la table ${tableName}:`, error)
      throw error
    }
  }

  /**
   * Synchronize a specific table
   */
  async syncDatabaseSyncTable(tableName, dryRun = false) {
    try {
      const data = await this.request(`/database-sync/tables/${tableName}/sync`, {
        method: 'POST',
        body: JSON.stringify({ dryRun })
      })
      return data.data || {}
    } catch (error) {
      console.error(`Erreur lors de la synchronisation de la table ${tableName}:`, error)
      throw error
    }
  }

  /**
   * Synchronize all tables
   */
  async syncAllDatabaseSyncTables(dryRun = false) {
    try {
      const data = await this.request('/database-sync/sync-all', {
        method: 'POST',
        body: JSON.stringify({ dryRun })
      })
      return data.data || {}
    } catch (error) {
      console.error('Erreur lors de la synchronisation de toutes les tables:', error)
      throw error
    }
  }

  /**
   * Test database connections
   */
  async testDatabaseSyncConnection() {
    try {
      const data = await this.request('/database-sync/test-connection')
      return data
    } catch (error) {
      console.error('Erreur lors du test de connexion aux bases de données:', error)
      throw error
    }
  }

  // ==================== VISIT SYNC TO MSSQL (MySQL → MSSQL) ====================

  /**
   * Get statistics for visit-related tables (MySQL as source, MSSQL as target).
   * Returns an array of { name, mysqlCount, mssqlCount, difference, status } for
   * visit, control and comment tables.
   */
  async getVisitSyncToMssqlStats() {
    try {
      const data = await this.request('/visit-sync-to-mssql/stats')
      return data.data || []
    } catch (error) {
      console.error('Erreur lors de la récupération des stats MySQL→MSSQL:', error)
      throw error
    }
  }

  /**
   * Synchronize all visit-related tables from MySQL to MSSQL.
   * @param {boolean} dryRun - If true, simulate without writing.
   */
  async syncVisitsToMssql(dryRun = false) {
    try {
      const data = await this.request('/visit-sync-to-mssql/sync', {
        method: 'POST',
        body: JSON.stringify({ dryRun })
      })
      return data.data || {}
    } catch (error) {
      console.error('Erreur lors de la synchronisation MySQL→MSSQL:', error)
      throw error
    }
  }

  /**
   * Synchronize a single visit-related table from MySQL to MSSQL.
   * @param {string} tableName - One of: visit, control, comment.
   * @param {boolean} dryRun - If true, simulate without writing.
   */
  async syncVisitTableToMssql(tableName, dryRun = false) {
    try {
      const data = await this.request(`/visit-sync-to-mssql/sync/${tableName}`, {
        method: 'POST',
        body: JSON.stringify({ dryRun })
      })
      return data.data || {}
    } catch (error) {
      console.error(`Erreur lors de la synchronisation MySQL→MSSQL de la table ${tableName}:`, error)
      throw error
    }
  }

  /**
   * Test database connections for the MySQL→MSSQL sync feature.
   */
  async testVisitSyncToMssqlConnection() {
    try {
      const data = await this.request('/visit-sync-to-mssql/test-connection')
      return data
    } catch (error) {
      console.error('Erreur lors du test de connexion MySQL→MSSQL:', error)
      throw error
    }
  }

  // ==================== DOUBLONS DE VISITES ====================

  /**
   * Analyse MSSQL et retourne les groupes de visites en doublon.
   * Un doublon = même lot + date/heure de visite à ±15 jours.
   * @param {string|null} fromDate - Date ISO YYYY-MM-DD à partir de laquelle filtrer (optionnel)
   * @returns {Promise<{success: boolean, data: Array, total: number, fromDate: string|null}>}
   */
  async getVisitDuplicates(fromDate = null) {
    try {
      const query = fromDate ? `?fromDate=${encodeURIComponent(fromDate)}` : ''
      const data = await this.request(`/visit-duplicates${query}`)
      return data
    } catch (error) {
      console.error('Erreur lors de la récupération des doublons de visites:', error)
      throw error
    }
  }

  /**
   * Supprime définitivement une visite (doublon) dans MSSQL et MySQL.
   * Les contrôles et commentaires associés sont également supprimés.
   * @param {string} visitId - UUID de la visite à supprimer
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteVisitDuplicate(visitId) {
    try {
      const data = await this.request(`/visit-duplicates/${visitId}`, {
        method: 'DELETE'
      })
      return data
    } catch (error) {
      console.error(`Erreur lors de la suppression de la visite doublon ${visitId}:`, error)
      throw error
    }
  }

  /**
   * Supprime définitivement une visite depuis la fiche visite.
   * Réservé à boris.parejo et igor.davidovitch (vérifié côté API via session JWT).
   * Les contrôles et commentaires associés sont également supprimés (cascade).
   * @param {string} visitId - UUID de la visite à supprimer
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteVisit(visitId) {
    try {
      const data = await this.request(`/visits/${visitId}`, {
        method: 'DELETE'
      })
      return data
    } catch (error) {
      console.error(`Erreur lors de la suppression de la visite ${visitId}:`, error)
      throw error
    }
  }

  // ─── Administration : Techniciens de visite ──────────────────────────────

  /**
   * Retourne la liste des lots dont le technicien attribué diffère
   * du technicien qui a effectué la dernière visite.
   *
   * @returns {Promise<{success: boolean, total: number, data: Array}>}
   */
  async getVisitManagerIssues() {
    try {
      return await this.request('/admin/visit-managers/issues')
    } catch (error) {
      console.error('Erreur lors de la récupération des discordances techniciens:', error)
      throw error
    }
  }

  /**
   * Corrige les lots en leur affectant le technicien de la dernière visite.
   * Met également à jour data_refreshed_at pour déclencher la re-sync PWA.
   *
   * @param {string[]|null} propertyIds - Liste de lots à corriger (null = tous)
   * @param {boolean}       dryRun      - true = simulation sans modification
   * @returns {Promise<{success: boolean, fixed: number, errors: Array, dryRun: boolean}>}
   */
  async fixVisitManagers(propertyIds = null, dryRun = false) {
    try {
      return await this.request('/admin/visit-managers/fix', {
        method: 'POST',
        body: JSON.stringify({ propertyIds, dryRun })
      })
    } catch (error) {
      console.error('Erreur lors de la correction des techniciens:', error)
      throw error
    }
  }

  // ─── Administration : Mandants ────────────────────────────────────────────

  /**
   * Récupère la liste de tous les mandants (ROLE_ADMIN uniquement)
   * @returns {Promise<Array>}
   */
  async getMandators() {
    try {
      return await this.request('/mandators', { method: 'GET' })
    } catch (error) {
      console.error('Erreur lors de la récupération des mandants:', error)
      throw error
    }
  }

  /**
   * Met à jour le delay_between_visits d'un mandant (ROLE_ADMIN uniquement)
   * @param {string} mandatorId - UUID du mandant
   * @param {number|null} delay - Délai en mois (1–120) ou null
   * @returns {Promise<{success: boolean, delayBetweenVisits: number|null}>}
   */
  async updateMandatorDelay(mandatorId, delay) {
    try {
      return await this.request(`/mandators/${mandatorId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delayBetweenVisits: delay })
      })
    } catch (error) {
      console.error(`Erreur mise à jour délai mandant ${mandatorId}:`, error)
      throw error
    }
  }

  /**
   * Prévisualise le nombre de propriétés vacantes affectées pour un mandant (ROLE_ADMIN uniquement)
   * @param {string} mandatorId - UUID du mandant
   * @returns {Promise<{success: boolean, affectedCount: number}>}
   */
  async previewUpdateScheduledVisits(mandatorId) {
    try {
      return await this.request(`/mandators/${mandatorId}/preview-update-visits`, {
        method: 'POST'
      })
    } catch (error) {
      console.error(`Erreur preview visites planifiées mandant ${mandatorId}:`, error)
      throw error
    }
  }

  /**
   * Applique la mise à jour de scheduled_visit_at pour un mandant (ROLE_ADMIN uniquement)
   * @param {string} mandatorId - UUID du mandant
   * @param {'null'|'recalculate'} mode - Mode de mise à jour
   * @param {Array<'mysql'|'mssql'>} targets - Bases de données cibles
   * @returns {Promise<{success: boolean, results: {mysql?: object, mssql?: object}}>}
   */
  async applyUpdateScheduledVisits(mandatorId, mode, targets) {
    try {
      return await this.request(`/mandators/${mandatorId}/update-scheduled-visits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, targets })
      })
    } catch (error) {
      console.error(`Erreur application update visites mandant ${mandatorId}:`, error)
      throw error
    }
  }

  // ─── Import de photos depuis serveur distant ──────────────────────────────

  /**
   * Récupère la liste des dernières visites par lot pour l'année N-1,
   * avec le statut de présence locale de chaque photo.
   * @param {number|null} year - Année cible (défaut : année courante - 1)
   * @returns {Promise<{year: number, total: number, data: Array}>}
   */
  async getLastVisitsN1(year = null) {
    try {
      const query = year ? `?year=${year}` : ''
      return await this.request(`/photo-import/last-visits-n1${query}`)
    } catch (error) {
      console.error('Erreur lors de la récupération des visites N-1:', error)
      throw error
    }
  }

  /**
   * Déclenche l'import (copie SFTP) des photos manquantes pour une visite.
   * @param {string} visitId - UUID de la visite
   * @returns {Promise<{success: boolean, message: string, copied: number, errors: Array}>}
   */
  async importVisitPhotos(visitId) {
    try {
      return await this.request(`/photo-import/import/${visitId}`, {
        method: 'POST'
      })
    } catch (error) {
      console.error(`Erreur import photos visite ${visitId}:`, error)
      throw error
    }
  }

  /**
   * Déclenche l'import en masse de toutes les photos manquantes pour toutes les visites N-1.
   * @param {number|null} year - Année cible (défaut : année courante - 1)
   * @returns {Promise<{success: boolean, message: string, totalMissing: number, copied: number, errors: Array}>}
   */
  async importAllPhotos(year = null) {
    try {
      return await this.request('/photo-import/import-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: year || new Date().getFullYear() - 1 })
      })
    } catch (error) {
      console.error('Erreur import en masse des photos:', error)
      throw error
    }
  }

  // ─── SQL Query explorer ───────────────────────────────────────────────────

  /**
   * Execute a raw SELECT query on MySQL or MSSQL database.
   * @param {'mysql'|'mssql'} connection - Target database connection
   * @param {string} query - SQL SELECT query to execute
   * @returns {Promise<{success: boolean, connection: string, columns: string[], rows: object[], rowCount: number, executionTime: number}>}
   */
  async executeSqlQuery(connection, query) {
    try {
      return await this.request('/sql-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connection, query })
      })
    } catch (error) {
      console.error('Erreur exécution requête SQL:', error)
      throw error
    }
  }

  // ─── Administration : lots avec date de prochaine visite incorrecte ───────

  /**
   * Récupère TOUS les lots triés par date de dernière visite DESC (la plus récente en premier).
   * Les lots sans visite apparaissent en dernier.
   * Utilisé par la page /admin/dernieres-visites.
   *
   * @param {number} page  - Numéro de page (commence à 1)
   * @param {number} limit - Nombre d'éléments par page (défaut : 30)
   * @param {string|null} nsil - Filtre optionnel sur le NSIL
   * @returns {Promise<{success: boolean, data: Array, meta: {page, limit, total, hasMore}}>}
   */
  async getAllPropertiesByLastVisit(page = 1, limit = 30, nsil = null, mandatorName = null) {
    try {
      let url = `/admin/properties/all-by-last-visit?page=${page}&limit=${limit}`
      if (nsil && nsil.trim() !== '') {
        url += `&nsil=${encodeURIComponent(nsil.trim())}`
      }
      if (mandatorName && mandatorName.trim() !== '') {
        url += `&mandatorName=${encodeURIComponent(mandatorName.trim())}`
      }
      return await this.request(url)
    } catch (error) {
      console.error('Erreur récupération lots triés par dernière visite:', error)
      throw error
    }
  }

  /**
   * Recalcule en batch UNE PAGE de propriétés.
   * Appeler en boucle (page 1, 2, 3…) jusqu'à ce que hasMore soit false.
   *
   * @param {number} page  - Page courante (commence à 1)
   * @param {number} limit - Nombre de lots par page (défaut : 100)
   * @returns {Promise<{success: boolean, message: string, processed: number, changed: number, errors: Array, total: number, currentPage: number, hasMore: boolean}>}
   */
  async recalculateAllIncorrect(page = 1, limit = 100) {
    try {
      return await this.request('/admin/properties/recalculate-all-incorrect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page, limit }),
      })
    } catch (error) {
      console.error('Erreur recalcul batch lots incorrects:', error)
      throw error
    }
  }

  /**
   * Met à jour la date de prochaine visite d'un lot.
   * Déclenche automatiquement data_refreshed_at et altaix_updated_at.
   *
   * @param {string} propertyId - UUID du lot
   * @param {string} scheduledVisitAt - Date au format YYYY-MM-DD
   * @returns {Promise<{success: boolean, scheduledVisitAt: string, dataRefreshedAt: string}>}
   */
  async updateAdminScheduledVisit(propertyId, scheduledVisitAt) {
    try {
      return await this.request(`/admin/properties/${propertyId}/scheduled-visit`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduledVisitAt })
      })
    } catch (error) {
      console.error('Erreur mise à jour date prochaine visite:', error)
      throw error
    }
  }

  /**
   * Lance le recalcul de la date de prochaine visite d'un lot via la ScheduledVisitChain.
   * Retourne la nouvelle date calculée, le diagnostic mis à jour et les timestamps.
   *
   * @param {string} propertyId - UUID du lot
   * @returns {Promise<{success: boolean, scheduledVisitAt: string, diagnostic: object, dataRefreshedAt: string}>}
   */
  async recalculateAdminScheduledVisit(propertyId) {
    try {
      return await this.request(`/admin/properties/${propertyId}/recalculate-scheduled-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
    } catch (error) {
      console.error('Erreur recalcul date prochaine visite:', error)
      throw error
    }
  }

  /**
   * Retourne les groupes de lots en doublon (même building_code + property_code).
   *
   * @returns {Promise<{success: boolean, totalGroups: number, totalDuplicateProperties: number, groups: Array}>}
   */
  async getPropertyDuplicates() {
    try {
      return await this.request('/admin/property-duplicates')
    } catch (error) {
      console.error('Erreur récupération doublons immeuble+lot:', error)
      throw error
    }
  }

  /**
   * Simule la suppression des visites excédentaires.
   * Ne supprime rien — lecture seule.
   *
   * @param {number} keepPerLot - Nombre de visites à conserver par lot (défaut : 4)
   * @returns {Promise<{success: boolean, keepPerLot: number, summary: object, details: Array}>}
   */
  async getVisitsCleanupSimulation(keepPerLot = 4) {
    try {
      return await this.request(`/admin/visits-cleanup/simulation?keepPerLot=${encodeURIComponent(keepPerLot)}`)
    } catch (error) {
      console.error('Erreur simulation nettoyage visites:', error)
      throw error
    }
  }

  // ─── Administration : fusion de lots en doublon ───────────────────────────

  /**
   * Prévisualise la fusion de lots sans effectuer aucune modification en base.
   * Retourne le détail des visites qui seraient transférées.
   *
   * @param {string}   keepId     - UUID du lot à conserver
   * @param {string[]} deleteIds  - UUIDs des lots à supprimer
   * @returns {Promise<{
   *   success: boolean,
   *   keepProperty: object,
   *   deleteProperties: object[],
   *   totalVisitsToMove: number,
   *   totalControlsToMove: number
   * }>}
   */
  async previewPropertyMerge(keepId, deleteIds) {
    try {
      return await this.request('/admin/property-merge/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keepId, deleteIds }),
      })
    } catch (error) {
      console.error('Erreur prévisualisation fusion lots:', error)
      throw error
    }
  }

  /**
   * Exécute la fusion : transfère les visites des lots supprimés vers le lot conservé,
   * puis soft-delete les lots supprimés (deleted_at + data_refreshed_at = NOW()).
   *
   * @param {string}   keepId     - UUID du lot à conserver
   * @param {string[]} deleteIds  - UUIDs des lots à supprimer
   * @returns {Promise<{
   *   success: boolean,
   *   visitsTransferred: number,
   *   propertiesDeleted: number,
   *   mergedAt: string
   * }>}
   */
  async executePropertyMerge(keepId, deleteIds) {
    try {
      return await this.request('/admin/property-merge/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keepId, deleteIds }),
      })
    } catch (error) {
      console.error('Erreur exécution fusion lots:', error)
      throw error
    }
  }

  // ─── Export Excel parc immobilier ─────────────────────────────────────────

  /**
   * Envoie une liste d'IDs de lots à l'API et retourne le Blob du fichier Excel.
   * Le payload JSON plain est utilisé directement (la limite Caddy est 50 MB,
   * largement suffisant pour ~1.1 MB de 29k UUIDs). La limite arbitraire de
   * 500 IDs côté API a été supprimée.
   *
   * @param {string[]} ids - UUIDs des lots sélectionnés
   * @returns {Promise<Blob>}  Blob de type .xlsx
   */
  async exportParcImmoExcel(ids) {
    const url = `${this.baseURL}/properties/export-excel`

    const { fetchToken } = await import('../utils/auth')
    const token = await fetchToken()

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ ids }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Erreur export : ${response.status} ${response.statusText}`)
    }

    // X-Export-Format est un header custom exposé via CORS (plus fiable que
    // Content-Type qui peut être réécrit par Caddy lors de la compression gzip)
    const exportFormat = response.headers.get('X-Export-Format') || 'xlsx'
    const blob = await response.blob()

    return { blob, exportFormat }
  }
  // ─────────────────────────────────────────────────────────────────────────
  // Photos base64
  // ─────────────────────────────────────────────────────────────────────────

  async getBase64Photos(page = 1, limit = 50, nsil = '') {
    const params = new URLSearchParams({ page, limit })
    if (nsil) params.set('nsil', nsil)
    return await this.request(`/admin/base64-photos?${params}`)
  }

  async fixBase64PhotosOne(visitId) {
    return await this.request(`/admin/base64-photos/${visitId}/fix`, { method: 'POST' })
  }

  async fixBase64PhotosAll(dryRun = false) {
    return await this.request('/admin/base64-photos/fix-all', {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    })
  }
}

export default new ApiService()
